/** Captação por pessoa da equipe: indicador (Indicadores › Financeiro) e ajuste em lote. */
import { useState } from "react";
import { keepPreviousData, useQuery } from "@tanstack/react-query";
import { api, qs } from "@/lib/api";
import { fmtMoney } from "@/lib/format";
import { useLookups } from "@/lib/catalog";
import { Card, ErrorBox, Field, Loading, Modal, UserSelect, useAct } from "./kit";

interface CaptRow { userId: string | null; name: string; billed: string; received: string; partners: string; net: string; overdue: string; avgTicket: string; cases: number; clients: number; pctBilled: number; pctReceived: number }
interface CaptData { from: string; to: string; rows: CaptRow[]; totals: { billed: string; received: string; partners: string; net: string; overdue: string }; missing: { clients: number; cases: number }; canBatch: boolean; note: string }

const COLORS = ["#34506F", "#5E9C82", "#E0B04F", "#8E7CC3", "#E07B6A", "#5AA9C9", "#B08968", "#B9C0CA"];
const pctTxt = (n: number) => `${n.toLocaleString("pt-BR", { maximumFractionDigits: 1 })}%`;

export function CaptacaoCard({ from, to, unitId }: { from: string; to: string; unitId?: string }): JSX.Element {
  const [metric, setMetric] = useState<"received" | "billed">("received");
  const [batch, setBatch] = useState(false);
  const { data, error } = useQuery({
    queryKey: ["captacao", from, to, unitId], placeholderData: keepPreviousData,
    queryFn: () => api.get<CaptData>(`/captacao/indicador${qs({ from, to, unitId: unitId || undefined })}`),
  });
  if (error) return <Card title="Captação por pessoa"><ErrorBox error={error} /></Card>;
  if (!data) return <Card title="Captação por pessoa"><Loading /></Card>;
  const pk = metric === "received" ? "pctReceived" : "pctBilled";
  return (
    <Card title="Receita por quem captou" actions={<>
      <div className="seg"><button className={metric === "received" ? "active" : ""} onClick={() => setMetric("received")}>Recebido</button><button className={metric === "billed" ? "active" : ""} onClick={() => setMetric("billed")}>Faturado (competência)</button></div>
      {data.canBatch && <button className="btn small" onClick={() => setBatch(true)}>Ajustar captação</button>}
    </>}>
      {(data.missing.clients > 0 || data.missing.cases > 0) && (
        <div className="draft-banner">Sem "Captado por": {data.missing.clients} cliente(s) e {data.missing.cases} caso(s) — a receita deles aparece como "Não informado".{data.canBatch && <button className="btn small" onClick={() => setBatch(true)}>Ajustar em lote</button>}</div>
      )}
      <div className="split-bar">
        {data.rows.map((r, i) => r[pk] > 0 && <span key={r.userId ?? "x"} style={{ width: `${r[pk]}%`, background: r.userId ? COLORS[i % COLORS.length] : "#D0D5DD" }} title={`${r.name}: ${pctTxt(r[pk])}`} />)}
      </div>
      <div className="table-wrap">
        <table className="table compact">
          <thead><tr><th>Quem captou</th><th className="right">Clientes novos</th><th className="right">Casos novos</th><th className="right">Faturado</th><th className="right">Recebido</th><th className="right">%</th><th className="right">Repasses</th><th className="right">Líquido</th><th className="right">Ticket médio</th><th className="right">Inadimplência</th></tr></thead>
          <tbody>
            {data.rows.map((r, i) => (
              <tr key={r.userId ?? "x"} className={r.userId ? "" : "row-muted"}>
                <td><i className="dot-sq" style={{ background: r.userId ? COLORS[i % COLORS.length] : "#D0D5DD" }} />{r.name}</td>
                <td className="right">{r.clients}</td><td className="right">{r.cases}</td>
                <td className="right">{fmtMoney(r.billed)}</td><td className="right">{fmtMoney(r.received)}</td><td className="right"><b>{pctTxt(r[pk])}</b></td>
                <td className="right">{fmtMoney(r.partners)}</td><td className="right">{fmtMoney(r.net)}</td><td className="right">{fmtMoney(r.avgTicket)}</td>
                <td className={`right ${Number(r.overdue) > 0 ? "text-bad" : ""}`}>{fmtMoney(r.overdue)}</td>
              </tr>
            ))}
            {!data.rows.length && <tr><td colSpan={10} className="muted small">Sem lançamentos no período.</td></tr>}
            <tr className="strong-row"><td>Total</td><td /><td /><td className="right">{fmtMoney(data.totals.billed)}</td><td className="right">{fmtMoney(data.totals.received)}</td><td className="right">100%</td><td className="right">{fmtMoney(data.totals.partners)}</td><td className="right">{fmtMoney(data.totals.net)}</td><td /><td className="right">{fmtMoney(data.totals.overdue)}</td></tr>
          </tbody>
        </table>
      </div>
      <div className="muted small" style={{ marginTop: 6 }}>{data.note}</div>
      {batch && <CaptacaoBatchModal onClose={() => setBatch(false)} />}
    </Card>
  );
}

interface PendingItem { id: string; label: string; sub: string; originUserId: string | null; clientOriginUserId?: string | null }

export function CaptacaoBatchModal({ onClose }: { onClose: () => void }): JSX.Element {
  const [entity, setEntity] = useState<"pessoa" | "caso">("pessoa");
  const [all, setAll] = useState(false);
  const [q, setQ] = useState("");
  const [sel, setSel] = useState<Set<string>>(new Set());
  const [userId, setUserId] = useState<string | null>(null);
  const [reason, setReason] = useState("");
  const [cascade, setCascade] = useState(true);
  const { userName } = useLookups();
  const { data, error } = useQuery({
    queryKey: ["captacao-pendentes", entity, all, q],
    queryFn: () => api.get<{ items: PendingItem[] }>(`/captacao/pendentes${qs({ entidade: entity, todos: all ? "1" : undefined, q: q || undefined })}`),
  });
  const items = data?.items ?? [];
  const changingExisting = items.some((i) => sel.has(i.id) && i.originUserId);
  const apply = useAct(() => api.post<{ updated: number; cases: number }>("/captacao/lote", { entity, ids: [...sel], userId, reason: reason || undefined, cascadeCases: cascade }),
    [["captacao-pendentes"], ["captacao"], ["pessoa"], ["caso"]], (r) => { setSel(new Set()); setReason(""); void r; }, "Captação atualizada");
  const toggle = (id: string) => { const n = new Set(sel); if (n.has(id)) n.delete(id); else n.add(id); setSel(n); };
  return (
    <Modal wide title="Ajustar “Captado por” em lote" onClose={onClose} footer={<>
      <span className="muted small" style={{ marginRight: "auto" }}>{sel.size} selecionado(s)</span>
      <button className="btn" onClick={onClose}>Fechar</button>
      <button className="btn primary" disabled={!sel.size || !userId || apply.isPending || (changingExisting && reason.trim().length < 3)} onClick={() => apply.mutate()}>Aplicar</button>
    </>}>
      <div className="toolbar">
        <div className="seg"><button className={entity === "pessoa" ? "active" : ""} onClick={() => { setEntity("pessoa"); setSel(new Set()); }}>Clientes</button><button className={entity === "caso" ? "active" : ""} onClick={() => { setEntity("caso"); setSel(new Set()); }}>Casos</button></div>
        <input className="input search" placeholder="Filtrar pelo nome…" value={q} onChange={(e) => setQ(e.target.value)} />
        <label className="check"><input type="checkbox" checked={all} onChange={(e) => { setAll(e.target.checked); setSel(new Set()); }} /> Mostrar também os que já têm captador</label>
      </div>
      <ErrorBox error={error} />
      <div className="table-wrap" style={{ maxHeight: "45vh", overflow: "auto" }}>
        <table className="table compact">
          <thead><tr><th style={{ width: 32 }}><input type="checkbox" checked={!!items.length && items.every((i) => sel.has(i.id))} onChange={(e) => setSel(e.target.checked ? new Set(items.map((i) => i.id)) : new Set())} /></th><th>{entity === "pessoa" ? "Cliente" : "Caso"}</th><th>{entity === "pessoa" ? "Casos" : "Cliente"}</th><th>Captado por hoje</th></tr></thead>
          <tbody>
            {items.map((i) => (
              <tr key={i.id} className="clickable" onClick={() => toggle(i.id)}>
                <td><input type="checkbox" checked={sel.has(i.id)} readOnly /></td>
                <td>{i.label}</td><td className="muted small">{i.sub}</td>
                <td>{i.originUserId ? userName(i.originUserId) : <span className="muted">—{i.clientOriginUserId ? ` (cliente: ${userName(i.clientOriginUserId)})` : ""}</span>}</td>
              </tr>
            ))}
            {data && !items.length && <tr><td colSpan={4} className="muted small">Nada pendente. 👌</td></tr>}
          </tbody>
        </table>
      </div>
      <div className="form-grid cols-3" style={{ marginTop: 12 }}>
        <Field label="Captado por" required><UserSelect value={userId} onChange={setUserId} emptyLabel="Escolha…" /></Field>
        <Field label={changingExisting ? "Motivo (obrigatório: há registros que já tinham captador)" : "Motivo (opcional)"} span={2}><input className="input" value={reason} onChange={(e) => setReason(e.target.value)} /></Field>
      </div>
      {entity === "pessoa" && <label className="check" style={{ marginTop: 8 }}><input type="checkbox" checked={cascade} onChange={(e) => setCascade(e.target.checked)} /> Aplicar também aos casos desses clientes que estão sem captador</label>}
      <ErrorBox error={apply.error} />
    </Modal>
  );
}
